import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';

// Keeps the notifications/unread-count React Query cache live via SSE.
// Uses the query-param auth workaround: native EventSource can't set an
// Authorization header, so the backend's AuthMiddleware accepts ?token=.
export function useNotificationStream() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const eventSourceRef = useRef<EventSource | null>(null);

    useEffect(() => {
        if (!user) {
            return;
        }

        const token = localStorage.getItem('auth_token');
        const apiBase = import.meta.env.VITE_API_BASE_URL || window.location.origin.replace('app.', 'api.');
        const formattedBase = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
        const url = `${formattedBase}/api/notifications/stream?token=${token}`;

        const es = new EventSource(url);
        eventSourceRef.current = es;

        // No error handling on purpose: EventSource reconnects itself after a
        // transient drop, and per spec it does NOT retry a non-2xx response
        // (expired token), so it closes on its own there too.
        es.addEventListener('notification', () => {
            // The DB row is the source of truth — refetch rather than trying
            // to splice the raw SSE payload into the cache by hand.
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });

        return () => {
            es.close();
        };
    }, [user, queryClient]);
}
